/**
 * Wartosci sortujace dla kolumn, ktore trzymaja juz sformatowany tekst
 * (kwoty "1 234,56", daty "28.10.2025 14:30", czasy "8d 12h 12m").
 * Porownanie takich napisow tekstem klamie, wiec kazda tabela sprowadza je tu do liczby.
 */
export const isEmptySortValue = (value: unknown): boolean => {
  if (value == null) return true
  if (Array.isArray(value)) return value.length === 0
  return String(value).trim() === ''
}

// Kolumny kwotowe trzymaja juz sformatowane napisy ("1 234,56", "123,00 / 456,00"),
// wiec Number() na nich zwraca NaN i sortowanie schodzilo do porownania tekstem
// (800,49 > 4072,68). Wyciagamy pierwsza liczbe i normalizujemy separatory.
const AMOUNT_REGEX = /-?\d[\d\s\u00a0\u202f.]*(?:,\d+)?/

export const parseAmountValue = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (value == null) return 0
  // Kolumny typu "stan przeliczen" trzymaja tablice 12 flag - sortujemy po liczbie
  // przeliczonych miesiecy, a nie po napisie "true,false,...".
  if (Array.isArray(value)) return value.filter(Boolean).length

  const raw = String(value).trim()
  if (!raw) return 0

  const direct = Number(raw)
  if (raw !== '' && Number.isFinite(direct)) return direct

  const match = raw.match(AMOUNT_REGEX)
  if (!match) return 0

  const normalized = match[0]
    .replace(/[\s\u00a0\u202f.]/g, '')
    .replace(',', '.')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

// Daty w tabelach sa renderowane jako DD.MM.YYYY [HH:mm], czego new Date() nie parsuje.
const DMY_REGEX =
  /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/

export const parseDateValue = (value: unknown): number => {
  if (value == null || value === '') return 0
  if (value instanceof Date) return value.getTime()

  const raw = String(value).trim()
  const match = raw.match(DMY_REGEX)
  if (match) {
    const [, day, month, year, hours, minutes, seconds] = match
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours ?? 0),
      Number(minutes ?? 0),
      Number(seconds ?? 0),
    ).getTime()
  }

  const parsed = new Date(raw).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

// Czasy trwania przychodza jako "8d 12h 12m" / "7h 29m" (FormatDuration na serwerze)
// albo jako .NET TimeSpan "1.02:30:00" / "09:15:00". W obu formatach porownanie
// tekstem klamie ("10h 0m" < "7h 29m"), wiec sprowadzamy je do minut.
const DHM_REGEX = /(\d+(?:[.,]\d+)?)\s*([dhms])/gi
const TIMESPAN_REGEX = /^(?:(\d+)\.)?(\d{1,3}):(\d{2})(?::(\d{2}))?$/
const DHM_UNIT_MINUTES: Record<string, number> = {
  d: 24 * 60,
  h: 60,
  m: 1,
  s: 1 / 60,
}

export const parseDurationValue = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (value == null) return 0

  const raw = String(value).trim()
  if (!raw) return 0

  const timespan = raw.match(TIMESPAN_REGEX)
  if (timespan) {
    const [, days, hours, minutes, seconds] = timespan
    return (
      Number(days ?? 0) * 24 * 60 +
      Number(hours) * 60 +
      Number(minutes) +
      Number(seconds ?? 0) / 60
    )
  }

  let total = 0
  let matched = false
  DHM_REGEX.lastIndex = 0
  let match = DHM_REGEX.exec(raw)
  while (match) {
    matched = true
    total +=
      Number(match[1].replace(',', '.')) *
      DHM_UNIT_MINUTES[match[2].toLowerCase()]
    match = DHM_REGEX.exec(raw)
  }

  return matched ? total : parseAmountValue(raw)
}
